/* eslint-disable no-void */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import * as Sentry from '@sentry/react';

import { WorksheetAiConfig } from 'types/Worksheets';
import { logBrazeEvent } from 'utils/brazeEvents';
import {
  getWorksheet,
  useCreateAiWorksheetFromArticle,
  useGetAiWorksheetRunStatus,
  Worksheet,
} from './useWorksheets';

type UseAiWorksheetGenerationResult = {
  generateWorksheet: (articleId: string, config: WorksheetAiConfig) => void;
  worksheet: Worksheet | null;
  isLoading: boolean;
  isPolling: boolean;
  progress: number;
  progressMessage: string;
  error: string | null;
  reset: () => void;
};

const OVERLOADED_MESSAGE =
  'KI-Dienst ist vorübergehend überlastet. Bitte versuche es in 10–15 Minuten erneut.';

const toErrorMessage = (failureReason: string | null): string => {
  if (failureReason?.includes('529') || failureReason?.includes('overloaded_error')) {
    return OVERLOADED_MESSAGE;
  }
  return failureReason || 'Worksheet generation failed';
};

const removeStoredRun = (storageKey: string | null): void => {
  if (!storageKey) return;
  try {
    window.localStorage.removeItem(storageKey);
  } catch (error) {
    Sentry.addBreadcrumb({
      category: 'localStorage',
      message: 'Failed to remove localStorage item',
      level: 'error',
      data: { error },
    });
    Sentry.captureException(error);
  }
};

const storeRun = (storageKey: string | null, runId: string): void => {
  if (!storageKey) return;
  try {
    window.localStorage.setItem(storageKey, JSON.stringify({ runId, startedAt: Date.now() }));
  } catch (error) {
    Sentry.addBreadcrumb({
      category: 'localStorage',
      message: 'Failed to set localStorage item',
      level: 'error',
      data: { error },
    });
    Sentry.captureException(error);
  }
};

export const useAiWorksheetGeneration = (
  articleIdForResume?: string,
  options?: { active?: boolean }
): UseAiWorksheetGenerationResult => {
  const [worksheet, setWorksheet] = useState<Worksheet | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [currentRunId, setCurrentRunId] = useState<string | null>(null);
  const isFetchingWorksheetRef = useRef(false);
  const isActive = options?.active ?? true;

  // New key: runs started before the workflow migration stored a job id here
  const storageKey = useMemo(
    () => (articleIdForResume ? `ai-worksheet-run:${articleIdForResume}` : null),
    [articleIdForResume]
  );

  const createMutation = useCreateAiWorksheetFromArticle();
  const statusMutation = useGetAiWorksheetRunStatus();

  // Tracks which worksheet id we've already fired 'worksheet_created' for, so
  // re-renders after completion don't duplicate the event.
  const trackedWorksheetIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (worksheet && trackedWorksheetIdRef.current !== worksheet.id) {
      trackedWorksheetIdRef.current = worksheet.id;
      logBrazeEvent('worksheet_created', { ai_generated: true });
    }
  }, [worksheet]);

  const stopRun = useCallback((): void => {
    setIsPolling(false);
    setCurrentRunId(null);
    removeStoredRun(storageKey);
  }, [storageKey]);

  const reset = useCallback((): void => {
    setWorksheet(null);
    setProgress(0);
    setProgressMessage('');
    setError(null);
    isFetchingWorksheetRef.current = false;
    stopRun();
  }, [stopRun]);

  const completeWithWorksheetId = useCallback(
    async (worksheetId: string): Promise<void> => {
      if (isFetchingWorksheetRef.current) return;
      isFetchingWorksheetRef.current = true;
      try {
        const result = await getWorksheet(worksheetId);
        if (!result) {
          throw new Error('Worksheet generation completed but no worksheet was returned');
        }
        setWorksheet(result);
        setProgress(100);
        setProgressMessage('Worksheet generated successfully!');
        stopRun();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load the generated worksheet');
        stopRun();
        Sentry.captureException(err);
      } finally {
        isFetchingWorksheetRef.current = false;
      }
    },
    [stopRun]
  );

  const pollRunStatus = useCallback(
    async (runId: string): Promise<void> => {
      // Avoid overlapping requests if a previous poll is still pending
      if (statusMutation.isPending) return;
      try {
        const result = await statusMutation.mutateAsync(runId);
        setProgress(result.progress);
        setProgressMessage(result.progressMessage);

        if (result.status === 'FAILED') {
          setError(toErrorMessage(result.failureReason));
          stopRun();
          return;
        }

        if (result.status === 'COMPLETED') {
          if (result.resultId) {
            await completeWithWorksheetId(result.resultId);
          } else {
            setError('Worksheet generation completed but no worksheet was returned');
            stopRun();
          }
        }
        // PENDING / PROCESSING: keep polling
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to check run status');
        stopRun();
        Sentry.captureException(err);
      }
    },
    [statusMutation, completeWithWorksheetId, stopRun]
  );

  // Polling effect
  useEffect(() => {
    if (!isPolling || !currentRunId || !isActive) return;

    const interval = setInterval(() => {
      void pollRunStatus(currentRunId);
    }, 4000);

    return () => {
      clearInterval(interval);
    };
  }, [isPolling, currentRunId, pollRunStatus, isActive]);

  // Auto-resume a running generation for this article (same browser) from localStorage
  useEffect(() => {
    if (!storageKey) return;
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (!raw) return;
      const parsed: { runId?: string } = JSON.parse(raw);
      if (parsed?.runId) {
        setCurrentRunId(parsed.runId);
        setIsPolling(true);
        setProgress((prev) => (prev > 0 ? prev : 5));
        setProgressMessage((prev) => prev || 'Fortschritt wird geladen...');
      }
      // Keep the storage key until completion/failure/reset
    } catch (err) {
      Sentry.addBreadcrumb({
        category: 'localStorage',
        message: 'Failed to get localStorage item',
        level: 'error',
        data: { error: err },
      });
      Sentry.captureException(err);
    }
    // Only run on mount/articleId change
  }, [storageKey]);

  const generateWorksheet = useCallback(
    async (articleId: string, config: WorksheetAiConfig): Promise<void> => {
      reset();
      try {
        const { runId } = await createMutation.mutateAsync({ articleId, config });
        setCurrentRunId(runId);
        setProgress(0);
        setProgressMessage('Anfrage wird verarbeitet...');
        setIsPolling(true);
        storeRun(storageKey, runId);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to start worksheet generation');
        Sentry.captureException(err);
      }
    },
    [createMutation, reset, storageKey]
  );

  return {
    generateWorksheet,
    worksheet,
    isLoading: createMutation.isPending || statusMutation.isPending,
    isPolling,
    progress,
    progressMessage,
    error,
    reset,
  };
};
